import {
  collection,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  query,
  where,
  orderBy,
  serverTimestamp,
  Timestamp,
  arrayUnion,
  arrayRemove,
  writeBatch
} from 'firebase/firestore';
import { db, auth } from '../firebase/config';

export interface PhotoComment {
  id: string;
  photoId: string;
  tripId: string;
  userId: string;
  userDisplayName: string;
  userPhotoURL?: string;
  text: string;
  createdAt: Date;
  updatedAt?: Date;
  isEdited: boolean;
  likes: string[]; // Array of user IDs who liked the comment
  replyToId?: string; // ID of the comment this is replying to
}

export interface CreateCommentData {
  photoId: string;
  tripId: string;
  text: string;
  replyToId?: string;
}

export interface UpdateCommentData {
  text: string;
}

/**
 * Get all comments for a specific photo
 */
export const getPhotoComments = async (photoId: string): Promise<PhotoComment[]> => {
  try {
    const commentsQuery = query(
      collection(db, 'photoComments'),
      where('photoId', '==', photoId),
      orderBy('createdAt', 'asc')
    );
    
    const querySnapshot = await getDocs(commentsQuery);
    const comments: PhotoComment[] = [];
    
    querySnapshot.forEach((doc) => {
      const data = doc.data();
      comments.push({
        id: doc.id,
        photoId: data.photoId,
        tripId: data.tripId,
        userId: data.userId,
        userDisplayName: data.userDisplayName,
        userPhotoURL: data.userPhotoURL,
        text: data.text,
        createdAt: data.createdAt.toDate(),
        updatedAt: data.updatedAt ? data.updatedAt.toDate() : undefined,
        isEdited: data.isEdited || false,
        likes: data.likes || [],
        replyToId: data.replyToId
      });
    });
    
    return comments;
  } catch (error) {
    console.error('Error getting photo comments:', error);
    throw error;
  }
};

/**
 * Add a new comment to a photo
 */
export const addPhotoComment = async (data: CreateCommentData): Promise<PhotoComment> => {
  try {
    // Get current user info
    const currentUser = auth.currentUser;
    if (!currentUser) {
      throw new Error('You must be logged in to comment');
    }
    
    // Check if photo exists
    const photoDoc = await getDoc(doc(db, 'photos', data.photoId));
    if (!photoDoc.exists()) {
      throw new Error('Photo not found');
    }
    
    // Create comment data
    const commentData = {
      photoId: data.photoId,
      tripId: data.tripId,
      userId: currentUser.uid,
      userDisplayName: currentUser.displayName || 'Anonymous',
      userPhotoURL: currentUser.photoURL || '',
      text: data.text,
      createdAt: serverTimestamp(),
      isEdited: false,
      likes: [],
      replyToId: data.replyToId || null
    };
    
    // Add comment to database
    const docRef = await addDoc(collection(db, 'photoComments'), commentData);
    
    // Update photo's comment count (optional)
    const photoRef = doc(db, 'photos', data.photoId);
    await updateDoc(photoRef, {
      commentCount: (photoDoc.data().commentCount || 0) + 1
    });
    
    // Return new comment
    return {
      id: docRef.id,
      ...commentData,
      createdAt: new Date(),
      likes: []
    } as PhotoComment;
  } catch (error) {
    console.error('Error adding photo comment:', error);
    throw error;
  }
};

/**
 * Update an existing comment
 */
export const updatePhotoComment = async (commentId: string, data: UpdateCommentData): Promise<void> => {
  try {
    const currentUser = auth.currentUser;
    if (!currentUser) {
      throw new Error('You must be logged in to edit a comment');
    }
    
    // Verify the comment exists and belongs to the current user
    const commentDoc = await getDoc(doc(db, 'photoComments', commentId));
    if (!commentDoc.exists()) {
      throw new Error('Comment not found');
    }
    
    const commentData = commentDoc.data();
    if (commentData.userId !== currentUser.uid) {
      throw new Error('You can only edit your own comments');
    }
    
    // Update the comment
    await updateDoc(doc(db, 'photoComments', commentId), {
      text: data.text,
      updatedAt: serverTimestamp(),
      isEdited: true
    });
  } catch (error) {
    console.error('Error updating photo comment:', error);
    throw error;
  }
};

/**
 * Delete a comment
 */
export const deletePhotoComment = async (commentId: string): Promise<void> => {
  try {
    const currentUser = auth.currentUser;
    if (!currentUser) {
      throw new Error('You must be logged in to delete a comment');
    }
    
    // Verify the comment exists and belongs to the current user
    const commentDoc = await getDoc(doc(db, 'photoComments', commentId));
    if (!commentDoc.exists()) {
      throw new Error('Comment not found');
    }
    
    const commentData = commentDoc.data();
    if (commentData.userId !== currentUser.uid) {
      throw new Error('You can only delete your own comments');
    }
    
    // Get any replies to this comment
    const repliesQuery = query(
      collection(db, 'photoComments'),
      where('replyToId', '==', commentId)
    );
    
    const repliesSnapshot = await getDocs(repliesQuery);
    
    // If there are replies, batch delete them all
    if (!repliesSnapshot.empty) {
      const batch = writeBatch(db);
      
      repliesSnapshot.forEach((replyDoc) => {
        batch.delete(replyDoc.ref);
      });
      
      // Also delete the main comment
      batch.delete(doc(db, 'photoComments', commentId));
      
      // Commit the batch
      await batch.commit();
    } else {
      // Just delete the comment if no replies
      await deleteDoc(doc(db, 'photoComments', commentId));
    }
    
    // Update photo's comment count
    const photoRef = doc(db, 'photos', commentData.photoId);
    const photoDoc = await getDoc(photoRef);
    
    if (photoDoc.exists()) {
      const currentCount = photoDoc.data().commentCount || 0;
      await updateDoc(photoRef, {
        commentCount: Math.max(0, currentCount - 1)
      });
    }
  } catch (error) {
    console.error('Error deleting photo comment:', error);
    throw error;
  }
};

/**
 * Toggle like on a comment
 */
export const toggleCommentLike = async (commentId: string): Promise<void> => {
  try {
    const currentUser = auth.currentUser;
    if (!currentUser) {
      throw new Error('You must be logged in to like a comment');
    }
    
    // Get the comment
    const commentRef = doc(db, 'photoComments', commentId);
    const commentDoc = await getDoc(commentRef);
    
    if (!commentDoc.exists()) {
      throw new Error('Comment not found');
    }
    
    const commentData = commentDoc.data();
    const likes = commentData.likes || [];
    
    // Check if the user has already liked the comment
    const userIndex = likes.indexOf(currentUser.uid);
    
    if (userIndex === -1) {
      // User hasn't liked the comment yet, so add their like
      await updateDoc(commentRef, {
        likes: arrayUnion(currentUser.uid)
      });
    } else {
      // User already liked the comment, so remove their like
      await updateDoc(commentRef, {
        likes: arrayRemove(currentUser.uid)
      });
    }
  } catch (error) {
    console.error('Error toggling comment like:', error);
    throw error;
  }
}; 